import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [years, setYears] = useState([]);
  const [winnersByYear, setWinnersByYear] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/winners/years`);
        const yearList = await res.json();
        setYears(yearList);

        const allData = {};
        await Promise.all(
          yearList.map(async (year) => {
            const r = await fetch(`${API_URL}/api/winners/year/${year}`);
            allData[year] = await r.json();
          })
        );
        setWinnersByYear(allData);
      } catch (err) {
        console.error('Failed to fetch winners:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="home-page" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-top">
            <div className="hero-left">
              <div className="hero-badge">
                <Award size={14} />
                <span>Est. 2007 - Global Recognition</span>
              </div>
              <h1 className="hero-title">
                Recognizing Excellence in <span className="highlight">Global Finance</span>
              </h1>
              <p className="hero-subtitle">
                The world's most prestigious international recognition for outstanding achievement and innovation in banking, forex, insurance, and investment sectors.
              </p>
              <div className="hero-cta-group">
                <Link to="/enter" className="hero-cta-primary" data-testid="enter-competition-btn">
                  <span>Enter Competition</span>
                  <ArrowRight size={18} />
                </Link>
                <Link to="/nominations" className="hero-cta-secondary" data-testid="view-categories-btn">
                  <span>View Categories</span>
                </Link>
              </div>
              <div className="hero-trust">
                <div className="trust-item">
                  <div className="trust-number">250+</div>
                  <div className="trust-label">Winners</div>
                </div>
                <div className="trust-item">
                  <div className="trust-number">35+</div>
                  <div className="trust-label">Countries</div>
                </div>
                <div className="trust-item">
                  <div className="trust-number">18</div>
                  <div className="trust-label">Years</div>
                </div>
              </div>
            </div>
            <div className="hero-right">
              <div className="hero-visual">
                <div className="award-trophy-container">
                  <img
                    src="/trophy.png"
                    alt="World Financial Award Trophy"
                    className="award-trophy-image"
                    data-testid="hero-trophy-image"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">98%</div>
              <div className="stat-label">Satisfaction</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">15+</div>
              <div className="stat-label">Categories</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">50K+</div>
              <div className="stat-label">Professionals</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">Global</div>
              <div className="stat-label">Recognition</div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Winners Sections */}
      {loading ? (
        <div className="winners-loading" data-testid="winners-loading">
          <div className="loading-spinner"></div>
          <p>Loading winners...</p>
        </div>
      ) : (
        years.map((year) => {
          const yearWinners = winnersByYear[year] || [];
          if (yearWinners.length === 0) return null;
          return (
            <section className="winners-section" key={year} data-testid={`winners-section-${year}`}>
              <h2 className="section-title">Winners of the World Financial Award {year}</h2>
              <div className="winners-grid">
                {yearWinners.map((winner, index) => (
                  <Link
                    to={`/company/${winner.slug}`}
                    key={`${winner.slug}-${index}`}
                    className="winner-card-link"
                    data-testid={`winner-link-${winner.slug}-${index}`}
                  >
                    <div className="winner-card">
                      <div className="winner-logo">
                        {winner.logo ? (
                          <img
                            src={winner.logo}
                            alt={winner.name}
                            className="winner-logo-img"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div className="winner-badge" style={winner.logo ? { display: 'none' } : {}}>
                          <div className="badge-icon"><Award size={36} color="#fff" /></div>
                        </div>
                        <h3 className="winner-name">{winner.name}</h3>
                      </div>
                      <p className="winner-award">
                        {winner.award_count > 1
                          ? `${winner.award_count} Awards`
                          : winner.nomination}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })
      )}

      {/* CTA to Hall of Fame */}
      {!loading && (
        <section className="hof-cta-section" data-testid="hof-cta">
          <div className="hof-cta-content">
            <h2>Explore Our Complete Hall of Fame</h2>
            <p>Browse all winners across every year, with detailed company profiles and award histories.</p>
            <Link to="/hall-of-fame" className="hero-cta-primary" data-testid="view-hall-of-fame-btn">
              <span>View Hall of Fame</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
